import { defineStore } from 'pinia'
import { useTokenStore } from './token'
import { useUrlNavigationStore } from './urlNavigation'
import { sortByOrderThenAlphabetic } from '~/utils/sorting'

// Define interfaces for type safety
interface User {
  id?: number;
  store_id?: number;
  country_id?: number;
  // Add other user properties as needed
}

interface Currency {
  id: number;
  iso_code: string;
  // Add other currency properties as needed
}

interface ParameterItem {
  key: string;
  fr: string;
  en: string;
  ordre?: number;
  category_code?: string;
  image?: string;
  parent_element_id?: number | null;
  sous_natures_codes?: string[] | null;
  // Add other parameter properties as needed
}

// Represents the state for parameters in the store
interface ParameterState {
  labels: Record<string, { fr: string; en: string }>;
  data: ParameterItem[];
}

interface Country {
  id: number;
  name: string;
  shipping_rates: Record<string, any> | string; // Can be JSON string or parsed object
  specific_params: { fr: string; en: string } | string;
  specific_params_lth: { fr: string; en: string } | string;
  specific_params_orfevrerie: { fr: string; en: string } | string;
  currency?: number;
  // Add other country properties as needed
}

interface Store {
  id: number;
  id_api: string;
  name: string;
  iso_code?: string;
  bank_information?: Record<string, any> | string; // Can be JSON string or parsed object
  additional_comments: { fr: string; en: string } | string;
  // Add other store properties as needed
}

// Generic API response structure, assuming data is always nested under a 'data' key
interface ApiResponse<T> {
  data: T;
  total?: number;
  limit?: number;
  skip?: number;
}

// For entities that are just a list in the response, not nested under 'data'
// interface ApiListResponse<T> extends Array<T> {}

interface LogsState {
  data: any[]; // Define more specifically if structure is known
  // Add other log state properties if any
}

// Interface for rights matrix
interface RightsMatrix {
  _meta?: {
    version: string;
    updated: string;
  };
  roles: Record<string, RoleRights>;
}

interface RoleRights {
  quotations?: Record<string, QuotationStatusRights>;
  users?: ResourceRights;
  stores?: ResourceRights;
  countries?: ResourceRights;
  currencies?: ResourceRights;
  parameters?: ResourceRights;
  products?: ResourceRights;
  role_hierarchy?: string[];
}

interface QuotationStatusRights {
  view?: number;
  save?: number;
  toReview?: number;
  validate?: number;
  decline?: number;
  order?: number;
  close?: number;
  finalize?: number;
  cancel?: number;
  uncancel?: number;
  revert?: number;
}

interface ResourceRights {
  view?: { allowed: number; scope?: string };
  create?: { allowed: number; scope?: string };
  edit?: { allowed: number; scope?: string };
  delete?: { allowed: number; scope?: string; if_not_used?: number; except_self?: number };
}

// Interface pour les notifications globales
interface Notification {
  show: boolean;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  icon?: string;
}

export const useGlobalStore = defineStore({
  id: 'global-store',
  state: () => {
    const config = useRuntimeConfig();
    const apiUrlFromEnv = config.public.apiUrl as string | undefined; // NUXT_PUBLIC_API_URL
    const projectKeyFromEnv = config.public.projectKey as string | undefined; // PROJECT_KEY

    let apiBaseUrl = apiUrlFromEnv;

    if (apiBaseUrl && apiBaseUrl.trim() !== '' && apiBaseUrl.trim() !== '/') { // Si NUXT_PUBLIC_API_URL est défini et non vide/juste un slash
    } else if (projectKeyFromEnv && (typeof window !== 'undefined' && window.location.hostname.includes('lwa.localhost'))) {
        // Spécifique pour l'environnement local Docker via Traefik si PROJECT_KEY est dispo
        const protocol = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'https' : 'http';
        apiBaseUrl = `${protocol}://api-${projectKeyFromEnv}.lwa.localhost/`;
    } else if (typeof window !== 'undefined') {
        // Fallback basé sur le hostname pour les autres environnements ou si lwa.localhost n'est pas détecté
        const hostname = window.location.hostname;
        if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
             // Ce cas devrait être moins fréquent si lwa.localhost est bien détecté avant
            apiBaseUrl = 'http://localhost:3030';
        } else {
            apiBaseUrl = "https://" + hostname;
        }
    } else {
      // Fallback ultime (SSR sans window, sans variables d'env robustes)
      console.warn("[GlobalStore] API URL could not be determined. Using fallback '/'.");
      apiBaseUrl = '/';
    }

    const normalizedApiBaseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl : `${apiBaseUrl}/`;
    // Ensure /api/ suffix is present if not already, especially for non-env var derived URLs
    const finalApiBase = normalizedApiBaseUrl.includes('/api/') ? normalizedApiBaseUrl : `${normalizedApiBaseUrl}api/`;

    // Configuration du DAM URL avec plus de flexibilité et debug
    let damUrl = '';
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;

      // Mapping des environnements
      if (hostname === "devismaison.dev.cloudh.io") {
        damUrl = "https://prd-dam.hermes.com/";
      } else if (hostname === "devismaison.rct.cloudh.io") {
        damUrl = "https://prd-dam.hermes.com/";
      } else if (hostname.includes('localhost') || hostname.includes('127.0.0.1') || hostname.includes('lwa.localhost')) {
        // Pour le développement local, utiliser le DAM de test
        damUrl = "https://prd-dam.hermes.com/";
      } else {
        // Production par défaut
        damUrl = "https://prd-dam.hermes.com/";
      }
    } else {
      // Fallback pour SSR
      damUrl = "https://prd-dam.hermes.com/";
    }

    // S'assurer que damUrl se termine par un slash
    if (damUrl && !damUrl.endsWith('/')) {
      damUrl = damUrl + '/';
    }

    // Initialize user from sessionStorage if available
    let initialUser = {} as User;
    if (typeof window !== 'undefined') {
      try {
        const loginStr = sessionStorage.getItem("myquote-login");
        if (loginStr) {
          const login = JSON.parse(loginStr);
          if (login && login.user) {
            initialUser = login.user;
          }
        }
      } catch (error) {
        console.error('[GlobalStore] Error initializing user from sessionStorage:', error);
      }
    }

    return {
      apiBase: finalApiBase,
      damUrl: damUrl,
      language: 'fr',
      currencies: { data: [] as Currency[] } as ApiResponse<Currency[]>,
      parameters: { labels: {}, data: [] as ParameterItem[] } as ParameterState,
      countries: { data: [] as Country[] } as ApiResponse<Country[]>,
      stores: { data: [] as Store[] } as ApiResponse<Store[]>,
      users: { data: [] as User[] } as ApiResponse<User[]>,
      logs: { data: [] as any[] } as LogsState,
      user: initialUser,
      isPrintingToPdf: false,
      loading: false,
      showAllColors: false, // État pour l'affichage complet de la liste des couleurs
      rightsMatrix: null as RightsMatrix | null, // Matrice des droits chargée depuis l'API
      // Système de notification global
      notification: {
        show: false,
        type: 'info',
        message: '',
        icon: ''
      } as Notification,
    };
  },
  actions: {
    init() {
      const savedLanguage = localStorage.getItem('myquote-language');
      if (savedLanguage && ['fr', 'en'].includes(savedLanguage)) {
        this.language = savedLanguage;
      } else {
        this.language = 'fr';
        localStorage.setItem('myquote-language', 'fr');
      }
    },
    updateLanguage(value: string) {
      if (!['fr', 'en'].includes(value)) {
        value = 'fr';
      }
      this.language = value;
      localStorage.setItem('myquote-language', value);
    },
    async updateUser(value: User) {
      this.user = value;
      if (this.user && typeof this.user.id === 'number') { // Stricter check for id
        await this.fetchRightsMatrix(true);
        await this.fetchStores(true);
        await this.fetchCountries(true);
        await this.fetchUsers(true);
        try { await this.fetchCurrencies(true); } catch (e) { console.warn('[GlobalStore] fetchCurrencies skipped (no permission):', e); }
        try { await this.fetchParameters(true); } catch (e) { console.warn('[GlobalStore] fetchParameters skipped (no permission):', e); }
      }
    },

    // Nouvelle méthode pour mettre à jour damUrl si nécessaire
    updateDamUrl(newDamUrl?: string) {
      if (newDamUrl) {
        const normalizedUrl = newDamUrl.endsWith('/') ? newDamUrl : `${newDamUrl}/`;
        this.damUrl = normalizedUrl;
      } else if (typeof window !== 'undefined') {
        // Recalculer damUrl basé sur l'hostname actuel
        const hostname = window.location.hostname;
        let calculatedDamUrl = '';

        if (hostname === "devismaison.dev.cloudh.io") {
          calculatedDamUrl = "https://tst-dam.hermes.com/";
        } else if (hostname === "devismaison.rct.cloudh.io") {
          calculatedDamUrl = "https://ppd-dam.hermes.com/";
        } else if (hostname.includes('localhost') || hostname.includes('127.0.0.1') || hostname.includes('lwa.localhost')) {
          calculatedDamUrl = "https://tst-dam.hermes.com/";
        } else {
          calculatedDamUrl = "https://prd-dam.hermes.com/";
        }

        if (calculatedDamUrl !== this.damUrl) {
          this.damUrl = calculatedDamUrl;
        }
      }
    },

    async fetchRightsMatrix(force = false) {
      if (force || !this.rightsMatrix) {
        const tokenStore = useTokenStore();
        try {
          const response = await $fetch<RightsMatrix>('/rights-matrix', {
            method: 'GET',
            headers: { "Authorization": "Bearer " + tokenStore.currentValue },
            baseURL: this.apiBase
          });

          if (response && response.roles) {
            this.rightsMatrix = response;
          } else {
            console.warn('[GlobalStore] fetchRightsMatrix - Response has unexpected structure:', response);
            this.rightsMatrix = null;
          }
        } catch (error) {
          console.error("[GlobalStore] fetchRightsMatrix - Error:", error);
          this.rightsMatrix = null;
        }
      }
    },

    async fetchParameters(force = false) {
      if (force || !this.parameters.data || this.parameters.data.length === 0) {
        const tokenStore = useTokenStore();
        try {
          // Use $fetch instead of $get to avoid useFetch warning after component mount
          const headers: Record<string, string> = {};
          // Only add Authorization header if token is available (for authenticated requests)
          if (tokenStore.currentValue) {
            headers["Authorization"] = "Bearer " + tokenStore.currentValue;
          }
          const response = await $fetch<ApiResponse<ParameterItem[]> | ParameterItem[]>('/parameters', {
            method: 'GET',
            headers,
            baseURL: this.apiBase
          });

          // Determine if response is { data: [...] } (Feathers) or [...] (direct array)
          let itemsToProcess: ParameterItem[] | undefined;

          if (Array.isArray(response)) {
            // Case 1: The /parameters endpoint returns the array directly
            itemsToProcess = response as ParameterItem[];
          } else if (response && typeof response === 'object' && 'data' in response && Array.isArray(response.data)) {
            // Case 2: The /parameters endpoint returns { data: [...] } (Feathers default)
            itemsToProcess = response.data as ParameterItem[];
          }

          if (itemsToProcess) {
            this.parameters.data = itemsToProcess;
            this.parameters.labels = {};
            itemsToProcess.forEach((param: ParameterItem) => {
              this.parameters.labels[param.key] = { "fr": param.fr, "en": param.en };
            });
          } else {
            console.error("[GlobalStore] fetchParameters - Data is not in expected format. response:", response);
            this.parameters.data = [];
            this.parameters.labels = {};
          }
        } catch (error) {
          console.error("[GlobalStore] fetchParameters - Error caught during fetch or processing:", error);
          if (error instanceof Error) {
            console.error("[GlobalStore] fetchParameters - Error object details:", {
              message: error.message,
              stack: error.stack,
              name: error.name
            });
          }
          this.parameters.data = [];
          this.parameters.labels = {};
        }
      }
    },

    async fetchCurrencies(force = false) {
      if (force || !this.currencies.data || this.currencies.data.length === 0) {
        const tokenStore = useTokenStore();
        try {
          const currsResponse = await $fetch<ApiResponse<Currency[]>>('/currency', {
            method: 'GET',
            headers: { "Authorization": "Bearer " + tokenStore.currentValue },
            baseURL: this.apiBase,
          });
          if (currsResponse && currsResponse.data) {
            this.currencies.data = currsResponse.data;
          }
        } catch (error) {
          console.error("fetchCurrencies - Error:", error);
        }
      }
    },

    async fetchUsers(force = false) {
      if (force || !this.users.data || this.users.data.length === 0) {
        const tokenStore = useTokenStore();
        try {
          // Utiliser $fetch au lieu de useFetch pour éviter le cache
          // useFetch met en cache les résultats, ce qui pose problème lors du changement d'utilisateur
          const response = await $fetch<ApiResponse<User[]>>('/users', {
            method: 'GET',
            headers: { "Authorization": "Bearer " + tokenStore.currentValue },
            baseURL: this.apiBase
          });

          if (response && response.data) {
            this.users = response;
          } else {
            console.error("fetchUsers - response or response.data is empty or undefined");
          }
        } catch (error) {
          console.error("fetchUsers - Error:", error);
        }
      }
    },

    async fetchCountries(force = false) {
      if (force || !this.countries.data || this.countries.data.length === 0) {
        const tokenStore = useTokenStore();
        try {
          const countriesResponse = await $fetch<ApiResponse<Country[]>>('/country', {
            method: 'GET',
            headers: { "Authorization": "Bearer " + tokenStore.currentValue },
            baseURL: this.apiBase
          });

          // Transformation des données
          if (countriesResponse && countriesResponse.data) {
            countriesResponse.data.forEach((country: Country) => {
              try {
                    country.shipping_rates = typeof country.shipping_rates === 'string' ? JSON.parse(country.shipping_rates) : country.shipping_rates || {};
                  } catch (e) { country.shipping_rates = {}; }
                  try {
                    country.specific_params = typeof country.specific_params === 'string' ? JSON.parse(country.specific_params as string) : country.specific_params || { fr: "", en: "" };
                  } catch (e) { country.specific_params = { fr: "", en: "" }; }
                  try {
                    country.specific_params_lth = typeof country.specific_params_lth === 'string' ? JSON.parse(country.specific_params_lth as string) : country.specific_params_lth || { fr: "", en: "" };
                  } catch (e) { country.specific_params_lth = { fr: "", en: "" }; }
                  try {
                    country.specific_params_orfevrerie = typeof country.specific_params_orfevrerie === 'string' ? JSON.parse(country.specific_params_orfevrerie as string) : country.specific_params_orfevrerie || { fr: "", en: "" };
                  } catch (e) { country.specific_params_orfevrerie = { fr: "", en: "" }; }
                });

            this.countries.data = countriesResponse.data;
          }
        } catch (error) {
          console.error("fetchCountries - Error:", error);
        }
      }
    },

    async fetchStores(force = false) {
      if (force || !this.stores.data || this.stores.data.length === 0) {
        const tokenStore = useTokenStore();
        try {
          const storesResponse = await $fetch<ApiResponse<Store[]>>('/store', {
            method: 'GET',
            headers: { "Authorization": "Bearer " + tokenStore.currentValue },
            baseURL: this.apiBase
          });

          // Transformation des données
          if (storesResponse && storesResponse.data) {
            storesResponse.data.forEach((store: Store) => {
              if (store.bank_information && typeof store.bank_information === 'string') {
                try {
                  const bi = JSON.parse(store.bank_information);
                  store.bank_information = bi;
                } catch (e) { store.bank_information = {}; }
              }
                  try {
                    store.additional_comments = typeof store.additional_comments === 'string' ? JSON.parse(store.additional_comments as string) : store.additional_comments || { fr: "", en: "" };
                  } catch (e) { store.additional_comments = { fr: "", en: "" }; }
                });

            this.stores.data = storesResponse.data;
          }
        } catch (error) {
          console.error("fetchStores - Error:", error);
        }
      }
    },

    getStoreName(id: number): string {
      if (this.stores && this.stores.data) {
        const store = this.stores.data.find((it: Store) => it.id == id);
        return (store?.iso_code) ? store.iso_code : (store?.name) ? store.name : "";
      }
      return "";
    },

    async fetchLogs(force = false) {
      if (force || !this.logs.data || this.logs.data.length === 0) {
        try {
          const fetchResult = await useNuxtApp().$get('/upload');
          if (fetchResult && fetchResult.data && 'value' in fetchResult.data) {
            const paginated = fetchResult.data.value;
            if (
              paginated &&
              typeof paginated === 'object' &&
              'data' in paginated &&
              Array.isArray((paginated as any).data)
            ) {
              this.logs.data = (paginated as any).data;
            } else {
              console.error("fetchLogs - Unexpected response structure from /upload:", fetchResult);
              this.logs.data = [];
            }
          } else {
            console.error("fetchLogs - fetchResult.data or fetchResult.data.value missing:", fetchResult);
            this.logs.data = [];
          }
        } catch (error) {
          console.error("fetchLogs - Error:", error);
          this.logs.data = [];
        }
      }
    },

    // =========================================================================
    // Système de notification global
    // =========================================================================

    /**
     * Affiche une notification de succès
     */
    showSuccess(message: string, icon = 'mdi-check-circle') {
      this.notification = {
        show: true,
        type: 'success',
        message,
        icon
      };
      // Auto-hide après 3 secondes pour les succès
      setTimeout(() => {
        this.hideNotification();
      }, 3000);
    },

    /**
     * Affiche une notification d'erreur (reste affichée jusqu'à fermeture manuelle)
     */
    showError(message: string, icon = 'mdi-alert-circle') {
      this.notification = {
        show: true,
        type: 'error',
        message,
        icon
      };
      // Pas d'auto-hide pour les erreurs - l'utilisateur doit fermer manuellement
    },

    /**
     * Affiche une notification d'avertissement
     */
    showWarning(message: string, icon = 'mdi-alert') {
      this.notification = {
        show: true,
        type: 'warning',
        message,
        icon
      };
      setTimeout(() => {
        this.hideNotification();
      }, 5000);
    },

    /**
     * Affiche une notification d'info
     */
    showInfo(message: string, icon = 'mdi-information') {
      this.notification = {
        show: true,
        type: 'info',
        message,
        icon
      };
      setTimeout(() => {
        this.hideNotification();
      }, 4000);
    },

    /**
     * Masque la notification
     */
    hideNotification() {
      this.notification.show = false;
    },

    /**
     * Extrait le message d'erreur d'une erreur API
     */
    extractErrorMessage(error: any): string {
      if (!error) return 'Erreur inconnue';
      if (error.data?.message) return error.data.message;
      if (error.message) return error.message;
      if (error.statusMessage) return error.statusMessage;
      if (error.response?._data?.message) return error.response._data.message;
      return typeof error === 'string' ? error : 'Erreur lors de l\'opération';
    },
  },
  getters: {
    getCountryNameById: (state) => {
      return (id: number): string => {
        if (state.countries && state.countries.data) {
          const country = state.countries.data.find((it: Country) => it.id == id);
          return country?.name || "";
        }
        return "";
      };
    },
    getStoreNameById: (state) => {
      return (id: number): string => {
        if (state.stores && state.stores.data) {
          const store = state.stores.data.find((it: Store) => it.id == id);
          return store?.name || "";
        }
        return "";
      };
    },
    getCurrencyCodeById: (state) => {
      return (id: number): string => {
        if (state.currencies && state.currencies.data) {
          const currency = state.currencies.data.find((it: Currency) => it.id == id);
          return currency?.iso_code || "";
        }
        return "";
      };
    },

    /** Devise ISO du pays rattaché à l'utilisateur connecté (fallback EUR). */
    getUserCurrencyCode: (state) => {
      return (): string => {
        try {
          const userCountryId = Number(state.user?.country_id);
          const userCountry = state.countries?.data?.find((it: Country) => it.id == userCountryId);
          const currencyId = Number(userCountry?.currency);
          if (!currencyId) return 'EUR';
          const currency = state.currencies?.data?.find((it: Currency) => it.id == currencyId);
          return currency?.iso_code || 'EUR';
        } catch {
          return 'EUR';
        }
      };
    },

    // Getter sûr pour damUrl
    getDamUrl: (state) => {
      if (!state.damUrl || state.damUrl === 'toto') {
        console.warn('[GlobalStore] damUrl is not properly initialized:', state.damUrl);
        // Fallback pour développement local
        if (typeof window !== 'undefined' && (
          window.location.hostname.includes('localhost') ||
          window.location.hostname.includes('127.0.0.1') ||
          window.location.hostname.includes('lwa.localhost')
        )) {
          return "https://tst-dam.hermes.com/";
        }
        return "https://prd-dam.hermes.com/";
      }
      return state.damUrl;
    },

    getNavigationMenuData: (state) => () => {
      const nuxtApp = useNuxtApp();
      const urlNavStore = useUrlNavigationStore();

      const menu = [];

      for (const catalogue of ['mobilier', 'porcelaine']) {
        // --- Categories ---
        // Filter only elements (categories without parent_element_id) to exclude sub-categories
        const categoriesParams = state.parameters.data.filter((p: ParameterItem) =>
          p.key === `ref_cat_${catalogue}` &&
          (!p.parent_element_id || p.parent_element_id === null)
        );
        const sortedCategories = sortByOrderThenAlphabetic(
          categoriesParams,
          (p: ParameterItem) => p.ordre,
          (p: ParameterItem) => nuxtApp.$getTranslation(p)
        );
        const categoryLinks = sortedCategories.map((c: ParameterItem) => ({
            label: nuxtApp.$getTranslation(c),
            to: urlNavStore.generateParameterUrl(c, catalogue)
        }));

        // --- Collections ---
        const collectionsParams = state.parameters.data.filter((p: ParameterItem) => p.key === `ref_col_${catalogue}`);
         const sortedCollections = sortByOrderThenAlphabetic(
          collectionsParams,
          (p: ParameterItem) => p.ordre,
          (p: ParameterItem) => nuxtApp.$getTranslation(p)
        );
        const collectionLinks = sortedCollections.map((c: ParameterItem) => ({
            label: nuxtApp.$getTranslation(c),
            to: urlNavStore.generateParameterUrl(c, catalogue)
        }));

        menu.push({
            label: nuxtApp.$getParameter(`ref_item_menu_${catalogue}`),
            children: [
                { label: nuxtApp.$getParameter("category"), children: categoryLinks },
                { label: nuxtApp.$getParameter("collection"), children: collectionLinks },
            ]
        });
      }

      menu.push({
        label: nuxtApp.$getParameter('quotes'),
        to: '/quote',
      });
      
      return menu;
    }
  }
})
